import { Capacitor } from '@capacitor/core';
import { CapacitorCookies } from '@capacitor/core';

class CookieService {
    async setCookie(key: string, value: string, options?: { path?: string; maxAge?: number; domain?: string }): Promise<void> {
        if (Capacitor.isNativePlatform()) {
            // Capacitor native (iOS/Android)
            await CapacitorCookies.setCookie({
                key: key,
                value: value,
                path: options?.path || '/',
                expires: options?.maxAge ? new Date(Date.now() + options.maxAge * 1000).toISOString() : undefined,
            });
        } else {
            // Web browser
            let cookieString = `${key}=${value}`;
            if (options?.path) cookieString += `; path=${options.path}`;
            if (options?.maxAge) cookieString += `; max-age=${options.maxAge}`;
            if (options?.domain) cookieString += `; domain=${options.domain}`;
            document.cookie = cookieString;
        }
    }

    async getCookie(key: string): Promise<string | null> {
        if (Capacitor.isNativePlatform()) {
            // Capacitor native (iOS/Android)
            const result = await CapacitorCookies.getCookies();
            return result[key] || null;
        } else {
            // Web browser
            const cookies = document.cookie.split(';');
            for (const cookie of cookies) {
                const [cookieKey, cookieValue] = cookie.trim().split('=');
                if (cookieKey === key) {
                    return cookieValue;
                }
            }
            return null;
        }
    }

    async hasCookie(key: string): Promise<boolean> {
        const value = await this.getCookie(key);
        return value !== null;
    }

    async deleteCookie(key: string, options?: { path?: string; domain?: string }): Promise<void> {
        if (Capacitor.isNativePlatform()) {
            // Capacitor native (iOS/Android)
            await CapacitorCookies.deleteCookie({
                key: key,
            });
        } else {
            // Web browser - set expiry to past
            let cookieString = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC`;
            if (options?.path) cookieString += `; path=${options.path}`;
            if (options?.domain) cookieString += `; domain=${options.domain}`;
            document.cookie = cookieString;
        }
    }

    async clearAllCookies(): Promise<void> {
        if (Capacitor.isNativePlatform()) {
            // Capacitor native (iOS/Android)
            await CapacitorCookies.clearAllCookies();
        } else {
            // Web browser - delete each cookie
            const cookies = document.cookie.split(';');
            for (const cookie of cookies) {
                const key = cookie.trim().split('=')[0];
                await this.deleteCookie(key);
            }
        }
    }
}

const cookieService = new CookieService();
export default cookieService;
